import { Fragment, useState } from 'react'
import { Box, ButtonBase, Typography, styled } from '@mui/material'
import { Prescription } from '../../../../types/models/Presc'
import { colorTokens } from '../../../../shared/styles/theme'
import { LAYOUT } from '../../../../shared/styles/spacing'
import { RADIUS } from '../../../../shared/styles/radii'
import { isQrOnly } from '../list/queue'
import {
  ACTION_LABELS,
  DIALOG_ACTIONS,
  describeRequest,
  failedMessage,
  isDialogAction,
} from '../remoteInput/model'
import { RemoteAction } from '../remoteInput/types'
import {
  ActVars,
  useRemoteInput,
  useRemoteInputActions,
} from '../remoteInput/useRemoteInput'
import { Note } from './Note'
import { NoteDialog } from './NoteDialog'

/**
 * 右端の文字の操作(design-book `action-row.md` の ActionLink の形: 13px / fg.muted /
 * hover overlay.hover)。主 CTA(青の面)は「レセコンに送信」1 つのままで、ここには色を使わない。
 */
const ActionText = ({
  label,
  onClick,
  disabled,
}: {
  label: string
  onClick: () => void
  disabled: boolean
}) => (
  <ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      minHeight: 24,
      px: 1,
      borderRadius: `${RADIUS.md}px`,
      fontSize: 13,
      fontWeight: 500,
      color: colorTokens.fg.muted,
      transition: 'background 120ms, color 120ms',
      '&:hover': {
        background: colorTokens.overlay.hover,
        color: colorTokens.fg.default,
      },
      '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
      '&.Mui-disabled': { color: colorTokens.fg.faint },
    }}
  >
    {label}
  </ButtonBase>
)

/** 途中で折り返さないまとまり(誰からの依頼・札と時刻・送信の有無)。改行はまとまりの間でだけ起こす */
const Chunk = styled('span')({ whiteSpace: 'nowrap' })

type DialogAction = keyof typeof DIALOG_ACTIONS

/**
 * 「遠隔」行。`FieldRow` と同じ 3 列(ラベル / 札と一言・申し送り / 右端の操作)で、
 * 項目の段の上(保険の上)に置く。開いてすぐ札と操作が見え、項目をスクロールしなくても押せる。
 *
 * 出し分けは `as_remote` / `as_requester` のどちらに紙があるかだけ:
 * - 頼まれた紙(遠隔の店): 「〇〇店から依頼 08:18」と一言(あれば)と 受領 / 断る / 完了
 * - 自店の紙で依頼先がある店: 札と 取り消し。依頼の行がまだ無ければ「遠隔へ」だけ
 * - 依頼先の無い店: 行そのものを出さない(gate は「出さない」で行う)
 *
 * 押せる操作は back の応答 `actions` を読む(表を写さない)。QR 直読の紙は「遠隔へ」を出さない —
 * 自動の依頼は OCR 経路だけで、QR 直読は項目が無く遠隔から再送もできない。
 *
 * 操作はどれも札を進めるだけで送信しない。窓(一言・申し送り)を挟むのは 遠隔へ・断る・完了 の 3 つ。
 * 通らなかったときは行の中で言い、toast にしない(Note.tsx)。
 */
export const RemoteInputRow = ({
  prescription,
}: {
  prescription: Prescription
}) => {
  const { overview, lookup } = useRemoteInput()
  const { act, isActing } = useRemoteInputActions()
  // 窓は mount したまま open で開け閉めする(FeedbackDialog と同じ閉じ方 = フェードが出る)。
  // 文言は最後に押した操作のまま残し、閉じる途中で題が変わらないようにする
  const [dialogAction, setDialogAction] = useState<DialogAction>('request')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [failedAction, setFailedAction] = useState<RemoteAction | null>(null)

  const eventId = prescription.ocr_request_event_id
  const paper = lookup(eventId)
  if (paper.side === null) return null
  if (!paper.request && isQrOnly(prescription)) return null

  // 依頼先が消えた店にも依頼の行は残る(戻った紙を送るため)。「遠隔へ」だけは依頼先が要る
  const hasRoute = !!overview?.route
  const actions: RemoteAction[] = paper.request
    ? paper.request.actions.filter((a) => a !== 'request' || hasRoute)
    : ['request']
  const text = paper.request
    ? describeRequest(
        paper.side === 'remote'
          ? paper
          : { side: 'requester', request: paper.request }
      )
    : null

  const toVars = (action: RemoteAction, note: string): ActVars =>
    action === 'request' || !paper.request
      ? { action: 'request', eventId, note }
      : { action, request: paper.request, note }

  const run = async (action: RemoteAction, note = '') => {
    setFailedAction(null)
    try {
      await act(toVars(action, note))
    } catch {
      setFailedAction(action)
    }
  }

  const press = (action: RemoteAction) => {
    if (isDialogAction(action)) {
      setFailedAction(null)
      setDialogAction(action)
      setIsDialogOpen(true)
      return
    }
    void run(action)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        minHeight: LAYOUT.blockRowMinHeight,
        py: 0.25,
      }}
    >
      <Typography
        sx={{
          width: 116,
          flexShrink: 0,
          pt: '4px',
          fontSize: 12,
          color: colorTokens.fg.muted,
        }}
      >
        遠隔
      </Typography>
      <Box
        sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
      >
        {text && (
          <>
            <Typography sx={{ pt: '4px', fontSize: 14, lineHeight: 1.45 }}>
              {text.head.map((part, i) => (
                <Fragment key={part}>
                  {i > 0 && ' · '}
                  <Chunk>{part}</Chunk>
                </Fragment>
              ))}
            </Typography>
            {text.note && (
              <Typography sx={{ fontSize: 14, lineHeight: 1.45 }}>
                {text.note}
              </Typography>
            )}
            {text.sent && (
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: colorTokens.fg.muted,
                }}
              >
                <Chunk>{text.sent}</Chunk>
              </Typography>
            )}
          </>
        )}
        {failedAction && <Note>{failedMessage(failedAction)}</Note>}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {actions.map((action) => (
          <ActionText
            key={action}
            label={ACTION_LABELS[action]}
            onClick={() => press(action)}
            disabled={isActing}
          />
        ))}
      </Box>
      <NoteDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={(note) => act(toVars(dialogAction, note))}
        allowEmpty
        {...DIALOG_ACTIONS[dialogAction]}
      />
    </Box>
  )
}
